import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {UIStateEnum} from "../../enum/UIStateEnum";
import {UserDetailsScreenUIState} from "../../enum/UserDetailsScreenUIState";
import {IRole} from "../../interfaces/IRole";
import {IAdminQueryResult} from "../../interfaces/IAdminQueryResult";
import {ICharity} from "../../interfaces/charity/ICharity";
import {IRolesGroupedByGame} from "../../interfaces/IRolesGroupedByGame";
import {MatDialog} from "@angular/material/dialog";
import {ActivatedRoute, Data, Router} from "@angular/router";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {DialogFunctionService} from "../../services/dialog-function.service";
import {PermissionsService} from "../../services/permissions.service";
import {SnackbarService} from "../../services/snackbar.service";
import {UsersService} from "../../services/users.service";
import {GameService} from "../../services/game.service";
import {concatMap, EMPTY, of} from "rxjs";
import {
  ConfirmationActionDialogComponent
} from "../../components/dialogs/confirmation-action-dialog/confirmation-action-dialog.component";
import {IRemoveRoleCommand} from "../../interfaces/IRemoveRoleCommand";
import {PermissionTypes} from 'src/app/enum/PermissionTypes';
import {RoleTableColumn} from "./role-table/role-table.component";
import {
  ColComponent,
  ContainerComponent,
  ListGroupDirective,
  ListGroupItemDirective,
  RowComponent
} from "@coreui/angular";
import {DatePipe, NgClass} from "@angular/common";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatCardModule} from "@angular/material/card";
import {MatTableModule} from "@angular/material/table";
import {MatIcon} from "@angular/material/icon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {MatTooltip} from "@angular/material/tooltip";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {AddUserRolesComponent} from "./add-user-roles/add-user-roles.component";
import {RoleTableComponent} from "./role-table/role-table.component";

/**
 * Route data configuration for UserDetailsComponent
 * Set these in your route definition:
 *
 * { path: 'user/:userId', component: UserDetailsComponent, data: { backUrl: '/users', useGameContext: false } }
 */
export interface UserDetailsRouteData extends Data {
  backUrl: string;           // URL to navigate back to (e.g., '/manage-cross-game-users')
  useGameContext?: boolean;  // Whether to use the active game context for API calls
}

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [
    MatIcon,
    ContainerComponent,
    MatButton,
    RowComponent,
    ColComponent,
    ListGroupDirective,
    ListGroupItemDirective,
    DatePipe,
    NgClass,
    MatCardModule,
    MatTableModule,
    MatIconButton,
    MatTooltip,
    HasPermissionDirective,
    MatProgressSpinner,
    AddUserRolesComponent,
    RoleTableComponent
  ],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss'
})
export class UserDetailsComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  private gameService = inject(GameService);

  // UI State signals
  uiState = signal<UIStateEnum>(UIStateEnum.ShowLoading);
  screenState = signal<UserDetailsScreenUIState>(UserDetailsScreenUIState.ViewDetails);

  // Template constants
  protected readonly uiStateForTemplate = UIStateEnum;
  protected readonly screenStateEnum = UserDetailsScreenUIState;
  protected readonly PermissionTypes = PermissionTypes;

  // Column configurations for role tables
  protected readonly roleColumns: RoleTableColumn[] = [
    { key: 'name', header: 'Role Name', getValue: (item: IRole) => item.Name },
    { key: 'description', header: 'Description', getValue: (item: IRole) => item.Description }
  ];

  protected readonly charityColumns: RoleTableColumn[] = [
    { key: 'name', header: 'Charity Name', getValue: (item: ICharity) => item.Name },
    { key: 'game', header: 'Game', getValue: (item: ICharity) => this.getGameNameById(item.GameId) }
  ];

  // Core data signal
  currentUser = signal<IAdminQueryResult | null>(null);

  // Computed signals for role organization
  crossGameRoles = computed(() => {
    const user = this.currentUser();
    if (!user?.Roles) return [];

    const roles: IRole[] = [];
    user.Roles.forEach(gameGroup => {
      gameGroup.GameRoles
        .filter(role => role.IsCrossGameRole)
        .forEach(role => {
          if (!roles.some(r => r.Id === role.Id)) {
            roles.push(role);
          }
        });
    });
    return roles;
  });

  gameSpecificRoles = computed(() => {
    const user = this.currentUser();
    if (!user?.Roles) return [];

    const groups: IRolesGroupedByGame[] = [];
    user.Roles.forEach(gameGroup => {
      const gameRoles = gameGroup.GameRoles.filter(role => !role.IsCrossGameRole);
      if (gameRoles.length > 0) {
        groups.push({
          GameId: gameGroup.GameId,
          GameName: gameGroup.GameName,
          GameRoles: gameRoles
        });
      }
    });
    return groups;
  });

  charityRoles = computed(() => {
    const user = this.currentUser();
    return user?.Charities || [];
  });

  // Game name lookup computed from current user
  private gameNameMap = computed(() => {
    const user = this.currentUser();
    const map = new Map<string, string>();
    if (user?.Roles) {
      user.Roles.forEach(gameGroup => {
        map.set(gameGroup.GameId, gameGroup.GameName);
      });
    }
    return map;
  });

  // Route configuration (read from route data)
  private routeConfig = signal<UserDetailsRouteData>({ backUrl: '/' });

  constructor(
    private matDialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private errorHandlingService: ErrorHandlingService,
    private dialogFunctionService: DialogFunctionService,
    private permissionsService: PermissionsService,
    private snackBarService: SnackbarService,
    private userService: UsersService
  ) {
  }

  ngOnInit() {
    // Read route configuration
    const routeData = this.activatedRoute.snapshot.data as UserDetailsRouteData;
    this.routeConfig.set(routeData);

    this.activatedRoute.paramMap
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        concatMap(paramMap => {
          const userId = paramMap.get('userId');
          if (userId) {
            this.currentUser.set(null);
            this.uiState.set(UIStateEnum.ShowLoading);
            return this.userService.fetchAdminByAdminId(userId, this.getGameIdForRequest());
          }
          return of(null);
        })
      )
      .subscribe({
        next: (user) => {
          this.currentUser.set(user);
          this.uiState.set(UIStateEnum.ShowData);
        },
        error: (err) => {
          this.uiState.set(UIStateEnum.ShowData);
          this.errorHandlingService.displayPageLevelErrorMessage(err);
        }
      });
  }

  /**
   * Gets the game ID for API requests based on route configuration
   */
  private getGameIdForRequest(): string | undefined {
    if (this.routeConfig().useGameContext) {
      return this.gameService.activeGame()?.Id;
    }
    return undefined;
  }

  /**
   * Gets the game name by game ID (for charity admin section)
   */
  protected getGameNameById(gameId: string): string {
    return this.gameNameMap().get(gameId) || 'Unknown Game';
  }

  /**
   * Handler for when roles are successfully added by the child component
   */
  onRolesAddedSuccessfully() {
    const user = this.currentUser();
    if (!user) return;

    this.uiState.set(UIStateEnum.ShowLoading);

    this.userService.fetchAdminByAdminId(user.Id, this.getGameIdForRequest()).subscribe({
      next: (updatedUser) => {
        this.currentUser.set(updatedUser);
        this.screenState.set(UserDetailsScreenUIState.ViewRoles);
        this.uiState.set(UIStateEnum.ShowData);
      },
      error: (err) => {
        this.uiState.set(UIStateEnum.ShowData);
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  /**
   * Handler for when the add roles process is cancelled
   */
  onAddRolesCancelled() {
    this.screenState.set(UserDetailsScreenUIState.ViewRoles);
  }

  // ========== Remove Role Methods ==========

  onRemoveCrossGameRoleClick(roleToRemove: IRole) {
    this.removeCrossGameRole(roleToRemove);
  }

  onRemoveGameRoleClick(roleToRemove: IRole, gameId: string) {
    this.removeGameSpecificRole(roleToRemove, gameId);
  }

  onRemoveCharityAdminClick(charity: ICharity) {
    this.removeCharityAdminAccess(charity);
  }

  /**
   * Removes a cross-game role from the current user
   */
  private removeCrossGameRole(roleToRemove: IRole) {
    const user = this.currentUser();
    if (!user) return;

    this.dialogFunctionService.setCallback(() => {
      const request: IRemoveRoleCommand = {
        roleIds: [roleToRemove.Id],
        targetAdminId: user.Id,
        gameId: undefined // No gameId for cross-game roles
      };

      return this.permissionsService.removeUserRole(request);
    });

    const dialogData = {
      title: 'Remove Cross-Game Role',
      message: `Are you sure you want to remove the ${roleToRemove.Name} cross-game role from ${user.FirstName} ${user.LastName}?`,
      successMessage: 'Successfully removed cross-game role',
      dataCyTag: 'confirm-remove-role-button'
    };

    this.openRemovalConfirmationDialog(dialogData, 'cross-game role');
  }

  /**
   * Removes a game-specific role from the current user
   */
  private removeGameSpecificRole(roleToRemove: IRole, gameId: string) {
    const user = this.currentUser();
    if (!user) return;

    this.dialogFunctionService.setCallback(() => {
      const request: IRemoveRoleCommand = {
        roleIds: [roleToRemove.Id],
        targetAdminId: user.Id,
        gameId: gameId
      };

      return this.permissionsService.removeUserRole(request);
    });

    const dialogData = {
      title: 'Remove Game Role',
      message: `Are you sure you want to remove the ${roleToRemove.Name} game role from ${user.FirstName} ${user.LastName}?`,
      successMessage: 'Successfully removed game role',
      dataCyTag: 'confirm-remove-role-button'
    };

    this.openRemovalConfirmationDialog(dialogData, 'game role');
  }

  /**
   * Removes charity admin access from the current user
   */
  private removeCharityAdminAccess(charity: ICharity) {
    const user = this.currentUser();
    if (!user) return;

    this.dialogFunctionService.setCallback(() => {
      const request: IRemoveRoleCommand = {
        roleIds: [], // Not used for charity admin removal
        targetAdminId: user.Id,
        charityId: charity.Id
      };

      return this.permissionsService.removeUserRole(request);
    });

    const dialogData = {
      title: 'Remove Charity Admin Access',
      message: `Are you sure you want to remove ${user.FirstName} ${user.LastName}'s admin access to ${charity.Name}?`,
      successMessage: 'Successfully removed charity admin access',
      dataCyTag: 'confirm-remove-role-button'
    };

    this.openRemovalConfirmationDialog(dialogData, 'charity admin access');
  }

  /**
   * Opens the confirmation dialog and handles the removal workflow
   */
  private openRemovalConfirmationDialog(dialogData: any, removalType: string) {
    const user = this.currentUser();
    if (!user) return;

    const dialogRef = this.matDialog.open(ConfirmationActionDialogComponent, {
      data: dialogData,
      width: '650px'
    });

    dialogRef.afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        concatMap(updateSuccessful =>
          updateSuccessful ? this.userService.fetchAdminByAdminId(user.Id, this.getGameIdForRequest()) : EMPTY
        )
      )
      .subscribe({
        next: (updatedUser) => {
          this.currentUser.set(updatedUser);
          this.snackBarService.openSuccessfulSnackBar(dialogData.successMessage);
        },
        error: () => {
          this.snackBarService.openErrorSnackBar(`Failed to remove ${removalType}. Please try again or contact a system admin`);
        }
      });
  }

  /**
   * Navigate back to the users list using route configuration
   */
  async onBackToUsersClick(): Promise<void> {
    const config = this.routeConfig();
    let backUrl = config.backUrl;

    // If using game context, prefix with game ID
    if (config.useGameContext) {
      const gameId = this.gameService.activeGame()?.Id;
      if (gameId) {
        backUrl = `${gameId}/${backUrl}`;
      }
    }

    await this.router.navigateByUrl(backUrl);
  }
}
